/**
 * PDF-Export für Prüfberichte (IDS, Hybrid, KI). Baut aus demselben Karten-Datenmodell, das auch
 * die Kartenansicht des Berichts speist (siehe `report-card-model.ts`), ein `pdfmake`-Dokument im
 * Stil der BIC-CI (siehe `ci-colors.ts`) – ein PDF liest sich linear, daher hier EIN Dokument für
 * den ganzen Bericht statt eines Tabellenblatts je Datei wie im Excel-Export; ein Seitenumbruch vor
 * jeder neuen Datei bildet die gleiche Gliederung nach.
 *
 * `pdfmake` läuft clientseitig; das globale `pdfMake`-Objekt wird über `angular.json` (Abschnitt
 * `scripts`) aus `pdfmake.min.js` + `vfs_fonts.js` geladen, kein Modul-Import nötig.
 */
import {NormalizedReport, ReportChip} from './normalized-report.model';
import {ReportDisplayConfig} from './report-display-config';
import {buildCardModel, CardBodyBlock, CardItem, CardModel, CardNode, CardStatusCount} from './report-card-model';
import {ReportStatus} from './report-filter';
import {CI_COLORS} from './ci-colors';
import {reportExportFileName, reportTypeLabel, reportTypeSubtitle} from './report-export-labels';
import {BIC_LOGO_HEIGHT, BIC_LOGO_WHITE_PNG_BASE64, BIC_LOGO_WIDTH} from './bic-logo';

declare const pdfMake: { createPdf(docDefinition: unknown): { download(fileName?: string): void } };

const BIC_LOGO_DATA_URI = `data:image/png;base64,${BIC_LOGO_WHITE_PNG_BASE64}`;
const LOGO_ASPECT_RATIO = BIC_LOGO_WIDTH / BIC_LOGO_HEIGHT;

/** ARGB (ExcelJS-Format) → Hex-Farbe (pdfmake-Format); alle CI_COLORS haben vollen Alpha-Kanal „FF". */
function hex(argb: string): string {
  return '#' + argb.slice(2);
}

const CI_HEX = {
  darkBlue: hex(CI_COLORS.darkBlue),
  turquoise: hex(CI_COLORS.turquoise),
  turquoiseLight: hex(CI_COLORS.turquoiseLight),
  orange: hex(CI_COLORS.orange),
  lightGray: hex(CI_COLORS.lightGray),
  white: hex(CI_COLORS.white),
  darkText: hex(CI_COLORS.darkText),
  red: hex(CI_COLORS.red),
  gray: '#6B6B6B',
};

function statusHex(status: ReportStatus): string {
  switch (status) {
    case 'success':
      return CI_HEX.turquoise;
    case 'warning':
      return CI_HEX.orange;
    case 'error':
      return CI_HEX.red;
    case 'unknown':
      return CI_HEX.gray;
  }
}

const STATUS_LABELS: Record<ReportStatus, string> = {
  success: 'Erfüllt', warning: 'Teilweise erfüllt', error: 'Nicht erfüllt', unknown: 'Unbekannt',
};

function clampPercent(percent: number | null | undefined): number {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) return 0;
  return Math.max(0, Math.min(percent, 100));
}

function formatDate(date: Date): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '–';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

/** Baut die `pdfmake`-Dokumentendefinition für den Bericht. */
export function buildCheckReportPdfDocDefinition(
  report: NormalizedReport,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
  overallStatus?: ReportStatus | null,
): unknown {
  // Kein aktiver Filter: der PDF-Export zeigt wie der Excel-Export immer den vollständigen
  // Bericht, unabhängig von der aktuellen Filterauswahl in der Berichtsansicht.
  const cardModel = buildCardModel(report, config, checkType, null);

  return {
    pageSize: 'A4',
    pageMargins: [40, 55, 40, 40],
    header: pageHeader(checkType),
    footer: pageFooter(report),
    content: [
      ...titleBlock(checkType),
      metaBlock(report, overallStatus),
      ...groupsContent(cardModel),
    ],
    defaultStyle: {fontSize: 9, color: CI_HEX.darkText},
  };
}

/**
 * Wiederkehrende Kopfzeile ab Seite 2: die große Titelleiste (siehe {@link titleBlock}) steht nur
 * auf Seite 1 im Inhalt, ohne laufenden Kopf wüsste man auf späteren Seiten sonst nicht mehr, um
 * welchen Bericht es sich handelt.
 */
function pageHeader(checkType: string | null | undefined): (currentPage: number) => unknown {
  return currentPage => {
    if (currentPage === 1) return {text: ''};
    return {
      table: {
        widths: ['*', 'auto'],
        body: [[
          {text: reportTypeLabel(checkType), color: CI_HEX.white, bold: true, fontSize: 10, fillColor: CI_HEX.darkBlue, margin: [40, 10, 0, 10]},
          {image: BIC_LOGO_DATA_URI, width: 44, height: 44 / LOGO_ASPECT_RATIO, fillColor: CI_HEX.darkBlue, margin: [0, 6, 40, 6]},
        ]],
      },
      layout: 'noBorders',
    };
  };
}

/** Wiederkehrende Fußzeile: Prüfregelset/-datum links, Seitenzahl rechts – auf jeder Seite. */
function pageFooter(report: NormalizedReport): (currentPage: number, pageCount: number) => unknown {
  return (currentPage, pageCount) => ({
    table: {
      widths: ['*', 'auto'],
      body: [[
        {
          text: [report.meta.rulesetName, formatDate(report.meta.checkDate)].filter(Boolean).join(' · '),
          fontSize: 7.5, color: CI_HEX.gray, margin: [40, 0, 0, 0],
        },
        {text: `Seite ${currentPage} von ${pageCount}`, fontSize: 7.5, color: CI_HEX.gray, margin: [0, 0, 40, 0]},
      ]],
    },
    layout: 'noBorders',
    margin: [0, 8, 0, 0],
  });
}

export async function downloadCheckReportPdf(
  report: NormalizedReport,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
  overallStatus?: ReportStatus | null,
  runName?: string | null,
): Promise<void> {
  const docDefinition = buildCheckReportPdfDocDefinition(report, config, checkType, overallStatus);
  const fileName = reportExportFileName(checkType, runName, report.meta.rulesetName, report.meta.checkDate, 'pdf');
  pdfMake.createPdf(docDefinition).download(fileName);
}

function titleBlock(checkType: string | null | undefined): unknown[] {
  const logoHeight = 34;
  return [
    {
      table: {
        widths: ['*', 'auto'],
        body: [[
          {
            text: reportTypeLabel(checkType), color: CI_HEX.white, bold: true, fontSize: 18,
            fillColor: CI_HEX.darkBlue, margin: [8, 8, 8, 8],
          },
          {
            image: BIC_LOGO_DATA_URI, width: logoHeight * LOGO_ASPECT_RATIO, height: logoHeight,
            fillColor: CI_HEX.darkBlue, margin: [0, 8, 12, 8],
          },
        ]],
      },
      layout: 'noBorders',
    },
    {
      table: {widths: ['*'], body: [[{
        text: reportTypeSubtitle(checkType), color: CI_HEX.darkBlue, italics: true, fontSize: 9,
        fillColor: CI_HEX.turquoiseLight, margin: [8, 4, 8, 4],
      }]]},
      layout: 'noBorders',
      margin: [0, 0, 0, 14],
    },
  ];
}

/**
 * Berichtsangaben über dem Inhalt.
 *
 * @param overallStatus Ampelstufe des Gesamtergebnisses (#2317). Ohne Angabe bleibt die Zahl
 *   dunkel wie bisher — die Stufe kennt nur die Berichtsansicht, weil dafür die Schwellwerte des
 *   Prüfregelsets nötig sind.
 */
function metaBlock(report: NormalizedReport, overallStatus?: ReportStatus | null): unknown {
  const overallLabel = 'Gesamtergebnis:';
  const rows: Array<[string, string]> = [
    ['Prüfung durchgeführt am:', formatDate(report.meta.checkDate)],
    ['myCheck:', report.meta.rulesetName || '–'],
    ...(report.meta.checkerName ? [['Prüfer*in:', report.meta.checkerName] as [string, string]] : []),
    ['Geprüfte Dateien:', String(report.meta.fileCount || report.groups.length || 0)],
    ['Geprüfte Prüfregeln:', String(report.meta.ruleCount || 0)],
    // „–" statt 0 %, wenn nichts bewertet wurde: eine Prozentzahl über einer Prüfung, die kein
    // Ergebnis ermittelt hat, ist eine Falschaussage.
    [overallLabel, report.meta.score == null
      ? '–'
      : `${Math.round(clampPercent(report.meta.score))}%`],
    ...(report.meta.unratedCount
      ? [['Bewertete Ergebnisse:',
        `${report.meta.ratedCount ?? 0} von ${(report.meta.ratedCount ?? 0) + report.meta.unratedCount}`,
      ] as [string, string]]
      : []),
  ];
  return {
    table: {
      widths: [130, '*'],
      body: rows.map(([label, value]) => [
        {text: label, fontSize: 9, color: CI_HEX.darkText},
        {
          text: value, fontSize: 9, bold: true,
          color: label === overallLabel && overallStatus
            ? statusHex(overallStatus)
            : CI_HEX.darkText,
        },
      ]),
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 16],
  };
}

/** Primärtext eines Chips (Label: Name, falls vorhanden, sonst Label: Wert) – wie die Berichtsansicht. */
function chipText(chip: ReportChip): string {
  const value = chip.name || chip.value;
  return chip.label ? `${chip.label}: ${value}` : value;
}

function countsLine(counts: ReadonlyArray<CardStatusCount>): unknown {
  if (counts.length === 0) return null;
  return {
    text: counts.map((count, i) => ({
      text: count.label + (i < counts.length - 1 ? '   ' : ''),
      fontSize: 8, color: statusHex(count.type),
    })),
    margin: [0, 0, 0, 4],
  };
}

/** Überschrift einer Gruppe (Datei) bzw. Sektion (Position): Titel, Status-Farbe, optionaler Badge. */
function nodeHeading(node: CardNode, fontSize: number): unknown {
  const runs: unknown[] = [{text: node.title, bold: true, fontSize, color: statusHex(node.status)}];
  if (node.badge) {
    runs.push({text: '   ' + node.badge.text, bold: true, fontSize: fontSize - 2, color: CI_HEX.gray});
  }
  if (node.showStatus) {
    runs.push({text: '   ' + STATUS_LABELS[node.status], fontSize: fontSize - 3, color: statusHex(node.status)});
  }
  return {text: runs, margin: [0, 10, 0, 2]};
}

function groupsContent(cardModel: CardModel): unknown[] {
  const content: unknown[] = [];
  cardModel.groups.forEach((group, groupIdx) => {
    if (groupIdx > 0) content.push({text: '', pageBreak: 'before'});

    // Ohne Titel (z. B. eine Position-Sektion für eine Datei ohne Positionen in einem gemischten
    // Bericht) wäre die Überschrift nur ein wiederholtes Badge/Zähler-Duplikat der Gruppe darüber.
    if (group.node?.title) {
      content.push(nodeHeading(group.node, 14));
      content.push(countsLine(group.counts));
    }

    group.sections.forEach(section => {
      if (section.node?.title) {
        content.push(nodeHeading(section.node, 12));
        content.push(countsLine(section.counts));
      }
      section.items.forEach(item => content.push(itemBlock(item)));
    });
  });
  return content.filter(Boolean);
}

function itemBlock(item: CardItem): unknown {
  const node = item.node;
  const stack: unknown[] = [];

  const titleRuns: unknown[] = [{text: node.title, bold: true, fontSize: 10.5}];
  if (node.badge) {
    titleRuns.push({
      text: '   ' + node.badge.text, bold: true, fontSize: 9,
      color: node.badge.type === 'info' ? CI_HEX.gray : statusHex(node.badge.type),
    });
  }
  if (node.showStatus) {
    titleRuns.push({text: '   ' + STATUS_LABELS[node.status], fontSize: 8, color: statusHex(node.status)});
  }
  stack.push({text: titleRuns, margin: [0, 8, 0, node.description ? 1 : 3]});

  if (node.description) {
    stack.push({text: node.description, italics: true, fontSize: 8.5, color: CI_HEX.gray, margin: [0, 0, 0, 3]});
  }

  const metaParts: string[] = [];
  if (node.secondaryText) metaParts.push(node.secondaryText);
  if (node.chips.length > 0) metaParts.push(node.chips.map(chipText).join(', '));
  if (metaParts.length > 0) {
    stack.push({text: metaParts.join('  ·  '), fontSize: 8, color: CI_HEX.gray, margin: [0, 0, 0, 4]});
  }

  item.body.forEach(block => {
    const rendered = bodyBlock(block);
    if (rendered) stack.push(rendered);
  });

  return {stack, margin: [0, 0, 0, 8]};
}

function bodyBlock(block: CardBodyBlock): unknown {
  switch (block.kind) {
    case 'step':
      return stepBlock(block);
    case 'facet':
      return facetBlock(block);
    case 'params':
      return paramsBlock(block);
    case 'raw':
      return rawBlock(block);
  }
}

function stepBlock(block: Extract<CardBodyBlock, { kind: 'step' }>): unknown {
  const stack: unknown[] = [];
  if (block.heading) {
    const headingRuns: unknown[] = [{
      text: block.heading, bold: true, fontSize: 9.5,
      color: block.status ? statusHex(block.status) : CI_HEX.darkText,
    }];
    if (block.status) {
      headingRuns.push({text: '   ' + STATUS_LABELS[block.status], fontSize: 8, color: statusHex(block.status)});
    }
    stack.push({text: headingRuns});
  }
  block.entries.forEach(entry => {
    stack.push({
      text: entry.label ? [{text: entry.label + ': ', bold: true}, {text: entry.value}] : entry.value,
      fontSize: 9, margin: [0, 1, 0, 0],
    });
  });
  if (block.ratingText) {
    stack.push({text: block.ratingText, fontSize: 8, color: CI_HEX.gray, margin: [0, 1, 0, 0]});
  }
  return {stack, margin: [10, 3, 0, 3]};
}

function facetBlock(block: Extract<CardBodyBlock, { kind: 'facet' }>): unknown {
  const titleRuns: unknown[] = [{text: block.title, bold: true, fontSize: 9.5, color: statusHex(block.badgeType)}];
  if (block.percentText) titleRuns.push({text: '   ' + block.percentText, fontSize: 8, color: CI_HEX.gray});
  if (block.status) {
    titleRuns.push({text: '   ' + STATUS_LABELS[block.status], fontSize: 8, color: statusHex(block.status)});
  }
  const stack: unknown[] = [{text: titleRuns}];
  if (block.chips.length > 0) {
    stack.push({text: block.chips.map(chipText).join(', '), fontSize: 8, color: CI_HEX.gray, margin: [0, 1, 0, 0]});
  }
  if (block.secondaryText) {
    stack.push({text: block.secondaryText, fontSize: 8, color: CI_HEX.gray, margin: [0, 1, 0, 0]});
  }
  // Muss dieselben Sätze zeigen wie die Kartenansicht (report-card-view.component.html), deshalb
  // kommt der Text bereits fertig aus formatFailureReason() in reasonTexts.
  block.reasonTexts?.forEach(reasonText => {
    stack.push({text: reasonText, fontSize: 8, color: CI_HEX.gray, margin: [0, 1, 0, 0]});
  });
  return {stack, margin: [10, 3, 0, 3]};
}

function paramsBlock(block: Extract<CardBodyBlock, { kind: 'params' }>): unknown {
  const stack: unknown[] = [{text: block.label, italics: true, fontSize: 8.5, color: CI_HEX.gray}];
  block.entries.forEach(entry => {
    stack.push({
      text: entry.label ? [{text: entry.label + ': ', bold: true}, {text: entry.value}] : entry.value,
      fontSize: 8.5, margin: [0, 1, 0, 0],
    });
  });
  return {stack, margin: [10, 3, 0, 3]};
}

/** Roh-JSON (nur außerhalb IDS/KI/Hybrid relevant, siehe `shouldShowRawOutput`); stark gekürzt. */
function rawBlock(block: Extract<CardBodyBlock, { kind: 'raw' }>): unknown {
  let json: string;
  try {
    json = JSON.stringify(block.json);
  } catch {
    return null;
  }
  const truncated = json.length > 500 ? json.slice(0, 500) + '…' : json;
  return {text: truncated, fontSize: 7, italics: true, color: CI_HEX.gray, margin: [10, 2, 0, 3]};
}
